import { memo, useCallback, useEffect, useState } from 'react';
import FlowEditorLegacy from '../../../js/components/flow/flow-editor';
import { GraphProvider } from '../../../js/components/graph/GraphProvider';
import { getLatest } from '../../sources/flow';
import { getUserSettings } from '../../sources/user';
import type { UserSettings } from '../../types/auth';
import type { NotificationType } from '../../types/notification';
import type { Tab } from '../../types/tab';
import { useFeatureFlag } from '../featureFlags/FeatureFlagProvider';
import { DebugConfigProvider } from '../flow/debug/DebugConfigProvider';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import { useTabsDispatch } from '../tabs/TabsProvider';
import FlowEditorSharedWrapper, { type FlowEditorSharedData } from './FlowEditorSharedWrapper';
import { UserSettingsProvider } from './UserSettingsProvider';

type Props = {
    tab: Tab;
    isActive: boolean;
    isTenantSelectorOpen: boolean;
};

const FlowTab = ({ tab, isActive, isTenantSelectorOpen }: Props) => {
    const { getFlag } = useFeatureFlag();
    const dispatchTabs = useTabsDispatch();
    const dispatchNotification = useNotificationDispatch();
    const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
    const notifyError = useCallback(
        (error: unknown) => {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        },
        [dispatchNotification],
    );
    const notifySuccess = useCallback(
        (message: string) => {
            dispatchNotification({ type: 'notifySuccess', payload: { message } });
        },
        [dispatchNotification],
    );
    const addNotification = useCallback(
        ({
            message,
            type,
            isPersistent = false,
        }: {
            message: string;
            type: NotificationType;
            isPersistent?: boolean;
        }) => {
            dispatchNotification({
                type: 'addNotification',
                payload: { type, message, isPersistent },
            });
        },
        [dispatchNotification],
    );

    const flowId = tab?.elementId;

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    const setTabTitle = useCallback(({ title, key }: { title: string; key: string }) => {
        dispatchTabs({
            type: 'SET_TITLE',
            payload: { tabKey: key, title },
        });
    }, []);

    useEffect(() => {
        if (!flowId) {
            return;
        }

        const getCanvasSettingsDefaults = () => ({
            lineThickness: 2.1,
            mapElementColorStyle: 'Color Borders',
        });

        const fetchUserSettings = async () => {
            try {
                const userSettings = await getUserSettings();
                userSettings.canvasSettings ??= getCanvasSettingsDefaults();

                setUserSettings(userSettings);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
                // Use default settings if fetching user settings fails
                setUserSettings({
                    canvasSettings: getCanvasSettingsDefaults(),
                });
            }
        };

        fetchUserSettings();
    }, [dispatchNotification, flowId]);

    useEffect(() => {
        if (!flowId) {
            return;
        }

        const getFlowName = async (flowId: string) => {
            const { developerName } = await getLatest(flowId).catch(() => {
                throw new Error('Could not open the requested Flow.');
            });
            return developerName;
        };

        getFlowName(flowId).then((name) => setTabTitle({ title: name, key: tab.key }));
    }, [flowId, tab.key, setTabTitle]);

    if (!flowId || !userSettings) {
        return null;
    }

    return (
        <UserSettingsProvider userSettings={userSettings}>
            <GraphProvider
                flowId={tab.elementId}
                isActive={isActive}
                addNotification={addNotification}
                isTenantSelectorOpen={isTenantSelectorOpen}
            >
                <DebugConfigProvider>
                    <FlowEditorSharedWrapper flowId={flowId}>
                        {({
                            releases,
                            environments,
                            loadReleases,
                            isLoadingReleases,
                        }: FlowEditorSharedData) => (
                            <FlowEditorLegacy
                                flowId={flowId}
                                tabKey={tab.key}
                                setTabTitle={setTabTitle}
                                releases={releases}
                                environments={environments}
                                loadReleases={loadReleases}
                                isLoadingReleases={isLoadingReleases}
                                getFlag={getFlag}
                                notifyError={notifyError}
                                notifySuccess={notifySuccess}
                                addNotification={addNotification}
                            />
                        )}
                    </FlowEditorSharedWrapper>
                </DebugConfigProvider>
            </GraphProvider>
        </UserSettingsProvider>
    );
};

export default memo(FlowTab);
